import classNames from 'classnames'
import type {Ref} from 'react'
import {memo, forwardRef, useMemo, useCallback, useEffect, useRef, Suspense} from 'react'
import {EntryPointContainer} from 'react-relay'

import {EntityProvider} from '../../../../contexts/entity'
import {useEntryPointLoaderWithEnvironment} from '../../../../hooks/relay/useEntryPointLoaderWithEnvironment'
import {useHash, useSetHash} from '../../../../hooks/routes'
import useEventCallback from '../../../../hooks/useEventCallback'
import {getBuild, getIsSakuraUI} from '../../../../selectors'
import {stringifyId} from '../../../../types'
import type {BuildId} from '../../../../types'
import {scrollIntoViewIfNeeded} from '../../../../utils/dom'
import Details from '../../Details/Details'
import RowGroup from '../../Grid/RowGroup'
import SvgIcon from '../../SvgIcon/SvgIcon'
import Build from '../Build/Build.container'

import type {Props} from './BuildDetails.types'
import {BuildDetailsContentEntryPoint} from './BuildDetailsContent/BuildDetailsContent.entryPoint'

import styles from './BuildDetails.css'

import {useAppSelector} from 'src/hooks/react-redux'

const BuildDetailsInner = memo(
  forwardRef(function BuildDetailsInner(props: Props, visibilityRef: Ref<HTMLDivElement>) {
    const {
      buildId,
      wrapped,
      nested,
      children,
      containerClassName,
      open,
      handleToggle,
      ...restProps
    } = props
    const scrollRef = useRef<HTMLDivElement>(null)

    const build = useAppSelector(state => getBuild(state, buildId))
    const expandable = useAppSelector(getIsSakuraUI)
    const queued = build?.state === 'queued'
    const {number} = build ?? {}
    useEffect(() => {
      if (expandable && open && scrollRef.current != null) {
        scrollIntoViewIfNeeded(scrollRef.current)
      } // Only needed on first render
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const renderBuild = useMemo(
      () => (
        <>
          <Build
            buildId={buildId}
            hideBorders={open}
            wrapped={wrapped}
            ref={visibilityRef}
            {...restProps}
          />
          {expandable && (
            <SvgIcon className={styles.arrow} icon={open ? 'chevron-up' : 'chevron-down'} />
          )}
        </>
      ),
      [buildId, expandable, open, restProps, visibilityRef, wrapped],
    )
    const [contentEntryPoint, loadContentEntryPoint, disposeContentEntryPoint] =
      useEntryPointLoaderWithEnvironment(BuildDetailsContentEntryPoint)
    useEffect(() => {
      if (open) {
        loadContentEntryPoint(buildId)
      }
    }, [buildId, loadContentEntryPoint, open])
    const renderDetails = useMemo(
      () => (
        <Details
          onToggle={isOpen => {
            if (isOpen) {
              loadContentEntryPoint(buildId)
            } else {
              disposeContentEntryPoint()
            }
            handleToggle?.(isOpen)
          }}
          open={open}
          headingClassName={styles.heading}
          summaryClassName={styles.button}
          detailsWrapperClassName={styles.detailsWrapper}
          renderButtonAside
          imitateHeading
          level={2}
          label={`Build${number != null ? ` #${number}` : ''}`}
          summary={renderBuild}
        >
          {contentEntryPoint != null && (
            <Suspense>
              <EntryPointContainer
                entryPointReference={contentEntryPoint}
                props={{buildId, queued}}
              />
            </Suspense>
          )}
        </Details>
      ),
      [
        open,
        number,
        renderBuild,
        contentEntryPoint,
        buildId,
        queued,
        handleToggle,
        loadContentEntryPoint,
        disposeContentEntryPoint,
      ],
    )
    const buildContainerClasses = classNames(containerClassName, styles.buildContainer, {
      [styles.open]: open,
      [styles.closed]: !open,
      [styles.wrapped]: wrapped,
      [styles.nested]: nested,
      [styles.queued]: queued,
      [styles.inHierarchy]: restProps.inHierarchy,
    })
    return (
      <EntityProvider buildId={buildId}>
        <div
          className={classNames(styles.container, {
            [styles.wrappedContainer]: wrapped,
          })}
        >
          <RowGroup span={4}>
            <div
              data-build-id={buildId}
              ref={scrollRef}
              key={stringifyId(buildId)}
              className={buildContainerClasses}
            >
              <>
                {expandable ? (
                  renderDetails
                ) : (
                  <>
                    {renderBuild}
                    <div className={styles.emptyRow} />
                  </>
                )}
              </>
              {children}
            </div>
          </RowGroup>
        </div>
      </EntityProvider>
    )
  }),
)

function BuildDetails(props: Props, visibilityRef: Ref<HTMLDivElement>) {
  const hash = useHash()
  const setHash = useSetHash()
  const onToggle = useCallback(
    (id: BuildId | null | undefined) => setHash(stringifyId(id)),
    [setHash],
  )
  const handleToggle = useEventCallback((isOpen: boolean) =>
    onToggle(isOpen ? props.buildId : null),
  )
  const open = hash === stringifyId(props.buildId)

  return (
    <BuildDetailsInner {...props} open={open} handleToggle={handleToggle} ref={visibilityRef} />
  )
}

export default memo(forwardRef(BuildDetails))
